import { useEffect, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { api } from '@/shared/axios-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { COMPONENTS_PAGE_INSET } from '@/components/smartai/ComponentsList/components-page-layout';
import AssistantThinkingLoader from './AssistantThinkingLoader';

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
}

const AssistantChatView = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setSending(true);
    try {
      const data = (await api.post('/chatbot/message', {
        session_id: sessionId,
        message: text,
      })) as { session_id: string; response: string };
      setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', text: data.response }]);
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', text: 'Sorry, I could not answer that. Please try again.' },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <div
        ref={listRef}
        className={cn(
          'min-h-0 flex-1 overflow-auto pb-24 pt-8 max-md:pb-20 max-md:pt-6',
          COMPONENTS_PAGE_INSET,
        )}>
        <div className="mx-auto flex min-h-full max-w-[42rem] flex-col justify-end gap-4">
          {messages.length === 0 && !sending ? (
            <p className="m-0 self-center text-center text-sm text-[var(--inv-text-muted)]">
              Ask about coverage, inventory, or onboarded components.
            </p>
          ) : null}
          {messages.map((message, index) => (
            <div
              key={`${message.role}-${index}`}
              className={cn(
                'max-w-[90%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed',
                message.role === 'user'
                  ? 'ml-auto bg-[var(--inv-accent)] text-[var(--inv-on-accent)]'
                  : 'mr-auto border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] text-[var(--inv-text)]',
              )}>
              {message.text}
            </div>
          ))}
          {sending ? <AssistantThinkingLoader /> : null}
        </div>
      </div>

      <form
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 bg-transparent pb-5 pt-10 max-md:pb-4',
          COMPONENTS_PAGE_INSET,
        )}
        onSubmit={event => {
          event.preventDefault();
          void send();
        }}>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-t from-[var(--inv-bg)] to-transparent"
        />
        <div
          className={cn(
            'pointer-events-auto mx-auto flex max-w-[42rem] items-center gap-2 rounded-full border border-solid',
            'border-[var(--inv-border)] bg-[var(--inv-surface)] py-1.5 pl-4 pr-1.5',
            'shadow-[0_12px_32px_-10px_rgba(45,42,38,0.2),0_2px_8px_rgba(45,42,38,0.06)]',
            'focus-within:border-[var(--inv-border-strong)] focus-within:shadow-[0_14px_36px_-10px_rgba(45,42,38,0.24),0_0_0_3px_var(--inv-accent-ring)]',
          )}>
          <Input
            value={draft}
            onChange={event => setDraft(event.target.value)}
            placeholder="Ask a question…"
            aria-label="Message"
            disabled={sending}
            autoComplete="off"
            className={cn(
              'h-9 border-0 bg-transparent px-0 shadow-none',
              'focus-visible:ring-0 disabled:bg-transparent',
            )}
          />
          <Button
            type="submit"
            size="sm"
            className="h-9 w-9 shrink-0 rounded-full px-0 hover:translate-y-0"
            disabled={sending || !draft.trim()}
            aria-label="Send">
            <Send className="h-4 w-4" strokeWidth={2} aria-hidden />
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AssistantChatView;
